import { Deferred } from '@inertiajs/react'
import {
  DocumentSkeleton, FormCardSkeleton, TableCardSkeleton,
} from '@/frontend/components/skeletons/ContentSkeletons'
import RotinasTab from '@/frontend/components/DigitalWorkers/RotinasTab'
import AllowlistTab from '@/frontend/components/DigitalWorkers/AllowlistTab'
import ConfigTab from '@/frontend/components/DigitalWorkers/ConfigTab'
import MemoriaFilePreview from '@/frontend/components/DigitalWorkers/memoria/MemoriaFilePreview'
import ConversationPanel from './ConversationPanel'
import EmptyConversationPanel from './EmptyConversationPanel'
import type { FleetSessionEvent } from '@/frontend/pages/CommandCenter/shared/ConversationView'
import type { Session, DigitalWorker, AllowlistData, ConfigData } from '../types'
import type { KnowledgeData, SelectedFile } from '@/frontend/components/DigitalWorkers/types'
import type { CronData } from '@/frontend/pages/Dashboard/DigitalWorkers/types'

interface Props {
  activeTab: 'sessoes' | 'rotinas' | 'memoria' | 'allowlist' | 'config'
  selectedFile: SelectedFile | null
  selectedSession: Session | null
  selectedEvents: FleetSessionEvent[]
  viewMode: string
  showThinking: boolean
  showToolCalls: boolean
  eventRole: string
  basePath: string
  currentParams: Record<string, string | number | undefined>
  knowledge?: KnowledgeData | null
  crons?: CronData | null
  allowlist?: AllowlistData | null
  telegramAllowlist?: AllowlistData | null
  config?: ConfigData | null
  digitalWorker: Pick<DigitalWorker, 'id' | 'whatsappProvider' | 'telegramBotUsername' | 'telegramConnectionStatus' | 'runtimeCapabilities'>
  fileContentUrl?: string | null
}

export default function TabContentPanel({
  activeTab, selectedFile, selectedSession, selectedEvents, viewMode,
  showThinking, showToolCalls, eventRole, basePath, currentParams,
  knowledge, crons, allowlist, telegramAllowlist, config, digitalWorker, fileContentUrl,
}: Props) {
  if (activeTab === 'memoria') {
    return (
      <div className="flex-1 overflow-y-auto p-4">
        <Deferred data="knowledge" fallback={<DocumentSkeleton />}>
          {selectedFile ? (
            <MemoriaFilePreview
              file={selectedFile}
              fileContentUrl={fileContentUrl ?? null}
              preload={knowledge?.defaultFile}
            />
          ) : (
            <div className="h-full flex items-center justify-center"><p className="text-sm text-muted-foreground">Selecione um arquivo para visualizar</p></div>
          )}
        </Deferred>
      </div>
    )
  }

  if (activeTab === 'allowlist') {
    if (!digitalWorker.runtimeCapabilities.whatsapp_cloud.supported && !digitalWorker.runtimeCapabilities.gateway_channels.supported) {
      return <p role="status" className="p-4 text-sm text-muted-foreground">{digitalWorker.runtimeCapabilities.gateway_channels.reason}</p>
    }
    return (
      <div className="flex-1 overflow-y-auto p-4">
        <Deferred data={["allowlist", "telegramAllowlist"]} fallback={<FormCardSkeleton fields={2} />}>
          <AllowlistTab
            allowlist={allowlist ?? null}
            telegramAllowlist={telegramAllowlist ?? null}
            hasWhatsApp={digitalWorker.runtimeCapabilities.whatsapp_cloud.supported && digitalWorker.whatsappProvider !== 'none'}
            hasTelegram={digitalWorker.runtimeCapabilities.gateway_channels.supported && !!digitalWorker.telegramBotUsername && digitalWorker.telegramConnectionStatus === 'active'}
            basePath={basePath}
          />
        </Deferred>
      </div>
    )
  }

  if (activeTab === 'rotinas') {
    return (
      <div className="flex-1 overflow-y-auto p-4">
        <Deferred data="crons" fallback={<TableCardSkeleton rows={4} />}>
          <RotinasTab crons={crons ?? null} />
        </Deferred>
      </div>
    )
  }

  if (activeTab === 'config') {
    return (
      <div className="flex-1 overflow-y-auto p-4">
        <Deferred data="config" fallback={<DocumentSkeleton lines={10} />}>
          <ConfigTab config={config ?? null} />
        </Deferred>
      </div>
    )
  }

  if (selectedSession) {
    return (
      <ConversationPanel
        session={selectedSession} events={selectedEvents} viewMode={viewMode}
        showThinking={showThinking} showToolCalls={showToolCalls} eventRole={eventRole}
        basePath={basePath} currentParams={currentParams}
      />
    )
  }

  return <EmptyConversationPanel />
}
